import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider, useQuery } from '@apollo/react-hooks';
import styled from 'styled-components';
import { gql } from 'apollo-boost';
import { Row, Col, Card, Skeleton } from 'antd';
import LazyLoad from 'react-lazyload';

import { SecondarySectionTitle } from '../../../components/styles';
import Layout from '../../../components/layout';
import { SettingProvider } from '../../../lib/context/settings';
import { AdsProvider } from '../../../lib/context/ads';
import { device } from '../../../lib/theme';

const { Meta } = Card;

const emptyCoupons = new Array(4).fill('');

const positionAbsolute: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const COUPONS_QUERY = gql`
  query Coupons {
    couponTypes(where: { nameLike: "Featured" }) {
      edges {
        node {
          carcareCoupons(where: { status: PUBLISH }) {
            edges {
              node {
                featuredImage {
                  node {
                    mediaItemUrl
                  }
                }
                title
                content
                uri
              }
            }
          }
        }
      }
    }
  }
`;

const SectionContainer = styled(Row)`
  padding: 8px 0;

  .ant-card-body {
    min-height: ${({ longForm }: { longForm: boolean }) =>
      longForm ? 300 : 160}px;
    position: relative;
  }
  .ant-card-meta-title {
    margin-bottom: 0 !important;
    text-overflow: ellipsis;
  }
  .ant-card-meta-description {
    font-family: 'CG-regular', sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: #2c2c2c;
    min-height: 140px;
  }

  .ant-card .coupon-action {
    background: transparent;
    border: none;
    color: #cd2127;
    font-family: 'CG-bold', sans-serif;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 24px;
  }

  .ant-card .coupon-action > img {
    margin-left: 8px;
  }

  .ant-carousel {
    width: 100%;
  }

  @media ${device.tablet} {
    padding: 64px 0;
  }
`;

const Coupons: React.FC<any> = () => {
  const { loading, error, data } = useQuery(COUPONS_QUERY);

  const displayRow =
    data && data.couponTypes.edges[0]
      ? data.couponTypes.edges[0].node.carcareCoupons.edges.map(
          ({ node: coupon }) => (
            <Col
              xs={{ span: 24 }}
              sm={{ span: 12 }}
              lg={{ span: 6 }}
              key={coupon.id}
            >
              <LazyLoad height={350} once>
                <Card
                  hoverable
                  cover={
                    <div
                      style={{
                        backgroundImage: `url(${coupon.featuredImage?.node?.mediaItemUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        paddingTop: '45%',
                      }}
                    />
                  }
                >
                  <Meta
                    title={
                      coupon.title ? (
                        <SecondarySectionTitle
                          marginBottom={16}
                          title={coupon.title}
                        >
                          {coupon.title}
                        </SecondarySectionTitle>
                      ) : null
                    }
                    description={
                      <div
                        dangerouslySetInnerHTML={{ __html: coupon.content }}
                      />
                    }
                  />
                  <a className="coupon-action" href={coupon.uri}>
                    Learn more
                    <img src="/img/icon/right-arrow.png" />
                  </a>
                </Card>
              </LazyLoad>
            </Col>
          )
        )
      : null;

  return (
    <div style={{ paddingTop: 50 }}>
      <div>
        <h1 style={{ marginTop: 0, marginBottom: -30, textAlign: 'center' }}>
          Coupons
        </h1>
        {loading ? (
          <Row>
            <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
              <SectionContainer
                gutter={[16, 16]}
                style={{ margin: 0 }}
                longForm={true}
              >
                {emptyCoupons.map((_, idx) => (
                  <Col xs={{ span: 12 }} lg={{ span: 6 }} key={idx}>
                    <Card
                      hoverable
                      cover={
                        <div
                          className="ant-skeleton ant-skeleton-active"
                          style={{
                            paddingTop: '45%',
                            position: 'relative',
                          }}
                        >
                          <div
                            className="ant-skeleton-content"
                            style={positionAbsolute}
                          >
                            <ul
                              className="ant-skeleton-paragraph"
                              style={{ ...positionAbsolute, marginBottom: 0 }}
                            >
                              <li
                                style={{
                                  ...positionAbsolute,
                                  height: 'auto',
                                }}
                              ></li>
                            </ul>
                          </div>
                        </div>
                      }
                    >
                      <Skeleton active />
                      <Skeleton.Button active />
                    </Card>
                  </Col>
                ))}
              </SectionContainer>
            </Col>
          </Row>
        ) : null}
        {error ? error.message : null}
        {!loading && !error ? (
          <Row>
            <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
              <SectionContainer
                gutter={[16, 16]}
                style={{ margin: 0 }}
                longForm
              >
                {displayRow}
              </SectionContainer>
            </Col>
          </Row>
        ) : null}
      </div>
    </div>
  );
};

const CouponsPage: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <Coupons />
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default CouponsPage;
